import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

interface FileLoadProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  disabled?: boolean;
}

const FileLoad: React.FC<FileLoadProps> = ({ onFileSelect, selectedFile, disabled = false }) => {
  const { t } = useTranslation();
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    multiple: false,
    maxSize: 10485760, // 10MB
    disabled
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
        ${isDragActive 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-300 hover:border-gray-400'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input {...getInputProps()} />
      
      {selectedFile ? (
        <div className="space-y-4">
          <div className="w-32 h-32 mx-auto">
            <img
              src={URL.createObjectURL(selectedFile)}
              alt="Preview"
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          <div>
            <p className="font-medium text-gray-900">{selectedFile.name}</p>
            <p className="text-sm text-gray-500">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} {t('fileLoad.fileSize')}
            </p>
          </div>
          {!disabled && (
            <p className="text-sm text-gray-500">
              {t('fileLoad.replace')}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">
              {t('fileLoad.title')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {isDragActive
                ? t('fileLoad.dragActive')
                : t('fileLoad.dragInactive')
              }
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {t('fileLoad.fileTypes')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileLoad;